// StudentUiVisibilityView — admin controla flags globais que afetam o que
// o aluno vê no painel:
//   - aiMetaPlatform: mostra "Gemini · gemini-2.5-flash" no Assistente IA
//                     quando a IA roda na plataforma
//   - aiMetaByok:     idem, quando o aluno usa a própria chave (BYOK)
//   - webhooks:       habilita a seção Webhooks dentro de Minha Conta
//
// Estado é guardado em `platform_config["student_ui_visibility"]`.
// [[project-student-ui-visibility]]

import { useEffect, useState } from "preact/hooks";
import {
  CheckCircle2,
  Cpu,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Webhook
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

interface Settings {
  aiMetaPlatform: boolean;
  aiMetaByok: boolean;
  webhooks: boolean;
}

export function StudentUiVisibilityView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Settings>({
    aiMetaPlatform: true,
    aiMetaByok: true,
    webhooks: true
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getStudentUiVisibility();
      setSaved(r.settings);
      setDraft(r.settings);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const r = await api.setStudentUiVisibility(draft);
      setSaved(r.settings);
      setDraft(r.settings);
      setSavedAt(Date.now());
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  const isDirty =
    saved !== null &&
    (draft.aiMetaPlatform !== saved.aiMetaPlatform ||
      draft.aiMetaByok !== saved.aiMetaByok ||
      draft.webhooks !== saved.webhooks);

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando configuração…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <div class="flex items-start gap-3">
          <Eye className="mt-0.5 size-5 shrink-0 text-violet-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Visibilidade no painel do aluno</h3>
            <p class="text-muted-foreground">
              Estes toggles controlam o que aparece para todos os alunos da
              plataforma. As mudanças passam a valer no próximo carregamento
              da sessão de cada aluno (próximo refresh ou novo login).
            </p>
          </div>
        </div>
      </div>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Cpu className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Rótulo de provider e modelo da IA</h2>
        </header>
        <p class="text-xs text-muted-foreground">
          No Assistente IA do Modo Estudo, mostramos para o aluno qual provider
          (Gemini, GPT, Claude, DeepSeek…) e qual modelo respondeu a mensagem.
          Você pode esconder essa informação separadamente para chamadas na
          plataforma e para chamadas com a chave do próprio aluno (BYOK).
        </p>

        <ToggleRow
          label="Mostrar quando a IA roda na plataforma"
          desc="Aluno vê a linha “gemini (plataforma) | gemini-2.5-flash” abaixo do nome da persona."
          checked={draft.aiMetaPlatform}
          onChange={(v) => setDraft({ ...draft, aiMetaPlatform: v })}
        />
        <ToggleRow
          label="Mostrar quando a IA roda com chave do aluno (BYOK)"
          desc="Quando o aluno cadastrou a própria chave (Gemini, OpenAI etc.) e ela está sendo usada."
          checked={draft.aiMetaByok}
          onChange={(v) => setDraft({ ...draft, aiMetaByok: v })}
        />
      </section>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Webhook className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Seção Webhooks em Minha Conta</h2>
        </header>
        <p class="text-xs text-muted-foreground">
          Cada aluno pode cadastrar webhooks pessoais para receber eventos
          (pagamento recebido, aula lançada etc.) em URLs externas. Se a sua
          organização não quer expor esse recurso, desligue aqui — a seção
          some inteira da página Minha Conta.
        </p>

        <ToggleRow
          label="Aluno pode configurar webhooks"
          desc="Quando ligado, a seção “Webhooks” aparece em Minha Conta. Webhooks já cadastrados continuam funcionando mesmo se você desligar mais tarde — só some da UI."
          checked={draft.webhooks}
          onChange={(v) => setDraft({ ...draft, webhooks: v })}
        />
      </section>

      <div class="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          {saving ? "Salvando…" : "Salvar alterações"}
        </button>
        {isDirty && (
          <button
            type="button"
            onClick={() => saved && setDraft(saved)}
            disabled={saving}
            class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        {savedAt && !isDirty && (
          <span class="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-3.5" /> salvo
          </span>
        )}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  desc,
  checked,
  onChange
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label class="flex items-start justify-between gap-3 rounded-md border border-border bg-background/50 px-3 py-2.5">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5 text-sm font-semibold">
          {checked ? (
            <Eye className="size-3.5 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <EyeOff className="size-3.5 text-muted-foreground" />
          )}
          {label}
        </div>
        <div class="mt-0.5 text-[11px] text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
